import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface RetiroVesAsTransaccionUsdt {
  idTransferencia: string;
  fechaInicio: string;
  fechaFondoWallet: string | null;
  fechaTermino: string | null;
  usuarioId: string;
  remitente: string;
  beneficiario: string | null;
  tn: number | null;
  montoCLP: number;
  montoUSDT: number;
  walletTRC20: string;
  network: string;
  estadoActual: string;
}

export interface RetiroVesAsTransaccionResponse {
  query: RetiroVesAsTransaccionUsdt[];
  count: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class BinanceWithdrawalsAdminService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    const errorMessage =
      error.error?.message || `Error del servidor: ${error.status}`;
    return throwError(() => errorMessage);
  }

  getAsTransaccionesUsdt(
    page: number,
    limit: number,
    soloPorProcesar = false,
  ): Promise<RetiroVesAsTransaccionResponse> {
    const url = `${this.baseUrl}binance-withdrawals/as-transacciones-usdt`;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    // `pending` es lo que Nest muestra como POR_PROCESAR; filtra y cuenta allá.
    if (soloPorProcesar) params = params.set('status', 'pending');

    const request$ = from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<RetiroVesAsTransaccionResponse>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
    return firstValueFrom(request$);
  }

  confirmWithdrawal(id: string, binanceTxId?: string): Promise<any> {
    const url = `${this.baseUrl}binance-withdrawals/${id}/confirm`;
    const body = binanceTxId ? { binanceTxId } : {};
    const request$ = from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, body, { headers })),
      catchError(this.handleError),
    );
    return firstValueFrom(request$);
  }

  /** Reenvía a Binance un retiro en POR_PROCESAR que ya fue una vez. */
  resendWithdrawal(id: string): Promise<any> {
    const url = `${this.baseUrl}binance-withdrawals/${id}/resend`;
    const request$ = from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, {}, { headers })),
      catchError(this.handleError),
    );
    return firstValueFrom(request$);
  }

  rejectWithdrawal(id: string, rejectionReason: string): Promise<any> {
    const url = `${this.baseUrl}binance-withdrawals/${id}/reject`;
    const request$ = from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<any>(url, { rejectionReason }, { headers }),
      ),
      catchError(this.handleError),
    );
    return firstValueFrom(request$);
  }
}
